import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import '../styles/professional-theme.css';
import { MapPin, Plus, X, ChevronRight, ChevronLeft, CheckCircle, Shield, Globe, Zap, ArrowLeft } from 'lucide-react';
import AppLogoFull from '../components/AppLogoFull';
import { DEFAULT_COUNTRIES, DEFAULT_CITIES_BY_COUNTRY } from '../lib/countries';
import { useLanguage } from '../contexts/LanguageContext';

const RegisterCompany = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Étape 1 : Infos compte
  const [accountData, setAccountData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  
  // Étape 2 : Infos entreprise
  const [companyData, setCompanyData] = useState({
    company_name: '',
    company_type: 'freight_forwarder',
    origin_country: '',
    transport_modes: ['aerien']
  });
  
  // Étape 3 : Zone de livraison par défaut
  const [deliveryZone, setDeliveryZone] = useState({
    destination_country: '',
    cities: ['']
  });

  const apiUrl = process.env.REACT_APP_BACKEND_URL;

  const addCity = () => {
    setDeliveryZone({
      ...deliveryZone,
      cities: [...deliveryZone.cities, '']
    });
  };

  const updateCity = (index, value) => {
    const newCities = [...deliveryZone.cities];
    newCities[index] = value;
    setDeliveryZone({
      ...deliveryZone,
      cities: newCities
    });
  };

  const removeCity = (index) => {
    if (deliveryZone.cities.length > 1) {
      const newCities = deliveryZone.cities.filter((_, i) => i !== index);
      setDeliveryZone({
        ...deliveryZone,
        cities: newCities
      });
    }
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError('');

      // Validation
      if (accountData.password !== accountData.confirmPassword) {
        setError(t('passwordsMismatch'));
        setLoading(false);
        return;
      }

      // Préparer les données
      const registrationData = {
        email: accountData.email,
        password: accountData.password,
        name: accountData.name,
        role: 'company_admin',
        company_data: {
          company_name: companyData.company_name,
          company_type: companyData.company_type,
          origin_country: companyData.origin_country,
          transport_modes: companyData.transport_modes,
          delivery_countries: [
            {
              country_name: companyData.origin_country,
              is_default: true,
              cities: []
            },
            {
              country_name: deliveryZone.destination_country,
              is_default: false,
              cities: deliveryZone.cities.filter(c => c.trim()).map(city => ({
                city_name: city.trim(),
                pickup_address: '',
                postal_code: ''
              }))
            }
          ]
        }
      };

      // Créer le compte
      const response = await fetch(`${apiUrl}/api/auth/register-company`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(registrationData)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || t('registrationError'));
      }

      // Succès
      setStep(4);
      setTimeout(() => {
        navigate('/login');
      }, 6000);
      
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Règle mot de passe : min 8 caractères, au moins une lettre et un chiffre.
  const passwordValid = (pwd) => /^(?=.*[A-Za-z])(?=.*\d).{8,}$/.test(pwd || '');
  const emailValid = (mail) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(mail || '');

  const canGoNext = () => {
    if (step === 1) {
      return (
        accountData.name.trim() &&
        emailValid(accountData.email) &&
        passwordValid(accountData.password) &&
        accountData.password === accountData.confirmPassword
      );
    }
    if (step === 2) {
      return companyData.company_name && companyData.origin_country && companyData.transport_modes.length > 0;
    }
    if (step === 3) {
      if (!deliveryZone.destination_country) return false;
      const cc = DEFAULT_COUNTRIES.find(c => c.name === deliveryZone.destination_country)?.code;
      const avail = cc ? (DEFAULT_CITIES_BY_COUNTRY[cc] || []) : [];
      // Pays sans villes prédéfinies : le pays suffit ; sinon au moins une ville choisie.
      return avail.length === 0 || deliveryZone.cities.some(c => c.trim());
    }
    return false;
  };

  // Étape 4 : Succès
  if (step === 4) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'hsl(var(--muted))',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 'var(--spacing-4)'
      }}>
        <div className="card-professional" style={{
          maxWidth: '500px',
          textAlign: 'center'
        }}>
          <div style={{
            width: '80px',
            height: '80px',
            borderRadius: '50%',
            background: 'hsl(var(--accent))',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto var(--spacing-4)',
            color: 'white'
          }}>
            <CheckCircle size={48} />
          </div>
          <h2 style={{
            fontSize: 'var(--text-3xl)',
            fontWeight: 'var(--font-bold)',
            color: 'hsl(var(--foreground))',
            marginBottom: 'var(--spacing-2)'
          }}>
            {t('registrationSuccess')}
          </h2>
          <p style={{ fontSize: 'var(--text-base)', color: 'hsl(var(--muted-foreground))', fontWeight: 600 }}>
            {t('redirectingToSignIn')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: 'hsl(var(--muted))',
      display: 'flex',
    }}>
      {/* Left panel — hero image + branding overlay */}
      <div className="hidden lg:flex" style={{ width: '45%', position: 'relative', overflow: 'hidden', background: 'linear-gradient(135deg, hsl(var(--accent)), hsl(222 47% 20%))' }}>
        <img
          src="https://images.unsplash.com/photo-1553413077-190dd305871c?w=1200&q=80&auto=format"
          alt=""
          loading="lazy"
          onError={(e) => { e.target.style.display = 'none'; }}
          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
        />
        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(0,0,0,0.8), rgba(0,0,0,0.3), rgba(0,0,0,0.15))' }} />

        <button
          type="button"
          onClick={() => navigate('/')}
          className="backdrop-blur-sm"
          style={{
            position: 'absolute',
            top: '1.5rem',
            left: '1.5rem',
            zIndex: 20,
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            fontSize: '0.875rem',
            fontWeight: 500,
            color: 'white',
            background: 'rgba(255,255,255,0.1)',
            border: '1px solid rgba(255,255,255,0.2)',
            borderRadius: '0.5rem',
            padding: '0.5rem 0.75rem',
            cursor: 'pointer',
          }}
        >
          <ArrowLeft size={16} />
          {t('home')}
        </button>

        <div style={{ position: 'relative', zIndex: 10, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', padding: '2rem', width: '100%' }}>
          <h2 style={{ fontSize: '1.75rem', fontWeight: 700, color: 'white', lineHeight: 1.3 }}>
            {t('joinFretManager')}
          </h2>
          <p style={{ marginTop: '0.75rem', color: 'rgba(255,255,255,0.8)', fontSize: '0.9375rem', maxWidth: '400px', lineHeight: 1.6 }}>
            {t('createYourSpace')}
          </p>

          <div style={{ marginTop: '1.5rem', display: 'flex', flexWrap: 'wrap', gap: '0.75rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', borderRadius: '9999px', background: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(4px)', padding: '0.5rem 1rem' }}>
              <Shield size={16} color="rgba(255,255,255,0.9)" />
              <span style={{ fontSize: '0.8125rem', color: 'rgba(255,255,255,0.9)' }}>{t('freeRegistration')}</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', borderRadius: '9999px', background: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(4px)', padding: '0.5rem 1rem' }}>
              <Globe size={16} color="rgba(255,255,255,0.9)" />
              <span style={{ fontSize: '0.8125rem', color: 'rgba(255,255,255,0.9)' }}>{t('multiCountryFromStart')}</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', borderRadius: '9999px', background: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(4px)', padding: '0.5rem 1rem' }}>
              <Zap size={16} color="rgba(255,255,255,0.9)" />
              <span style={{ fontSize: '0.8125rem', color: 'rgba(255,255,255,0.9)' }}>{t('operationalIn5Min')}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Right panel — form */}
      <div style={{
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 'var(--spacing-4)',
        position: 'relative',
      }}>
      <div className="card-professional" style={{
        maxWidth: '600px',
        width: '100%'
      }}>
        {/* Mobile back button (left panel hidden on mobile) */}
        <button
          type="button"
          onClick={() => navigate('/')}
          className="lg:hidden"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            fontSize: '0.875rem',
            fontWeight: 500,
            color: 'hsl(var(--muted-foreground))',
            background: 'none',
            border: 'none',
            padding: '0',
            marginBottom: 'var(--spacing-4)',
            cursor: 'pointer',
          }}
        >
          <ArrowLeft size={16} />
          {t('home')}
        </button>

        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: 'var(--spacing-6)' }}>
          <div style={{ display: 'flex', justifyContent: 'center', margin: '0 auto var(--spacing-3)' }}>
            <AppLogoFull height={44} />
          </div>
          <h1 className="text-xl sm:text-3xl" style={{
            fontWeight: 'var(--font-bold)',
            color: 'hsl(var(--foreground))',
            marginBottom: 'var(--spacing-2)'
          }}>
            {t('createYourCompanyTitle')}
          </h1>
          <p style={{ fontSize: 'var(--text-sm)', color: 'hsl(var(--muted-foreground))' }}>
            {t('stepXofTotal').replace('{step}', step)}
          </p>
        </div>

        {/* Progress Bar */}
        <div style={{
          display: 'flex',
          gap: 'var(--spacing-2)',
          marginBottom: 'var(--spacing-6)'
        }}>
          {[1, 2, 3].map(s => (
            <div
              key={s}
              style={{
                flex: 1,
                height: '4px',
                borderRadius: '2px',
                background: s <= step ? 'var(--accent)' : 'hsl(var(--border))',
                transition: 'all 0.3s ease'
              }}
            />
          ))}
        </div>

        {/* Error Message */}
        {error && (
          <div style={{
            padding: 'var(--spacing-4)',
            background: 'var(--danger-50)',
            border: '1px solid var(--danger)',
            borderRadius: 'var(--radius)',
            color: 'var(--danger-700)',
            marginBottom: 'var(--spacing-6)',
            fontSize: 'var(--text-sm)'
          }}>
            {error}
          </div>
        )}

        {/* Étape 1 : Compte */}
        {step === 1 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-5)' }}>
            <h3 style={{
              fontSize: 'var(--text-xl)',
              fontWeight: 'var(--font-bold)',
              color: 'hsl(var(--foreground))',
              marginBottom: 'var(--spacing-2)'
            }}>
              {t('accountInfo')}
            </h3>

            <div>
              <label style={{
                display: 'block',
                fontSize: 'var(--text-sm)',
                fontWeight: 'var(--font-semibold)',
                color: 'hsl(var(--foreground))',
                marginBottom: 'var(--spacing-2)'
              }}>
                {t('fullNameRequired')}
              </label>
              <input
                type="text"
                value={accountData.name}
                onChange={(e) => setAccountData({ ...accountData, name: e.target.value })}
                placeholder={t('yourName')}
                className="w-full text-sm sm:text-base rounded-md border border-border px-3 py-2.5 sm:px-4 sm:py-3"
              />
            </div>

            <div>
              <label style={{
                display: 'block',
                fontSize: 'var(--text-sm)',
                fontWeight: 'var(--font-semibold)',
                color: 'hsl(var(--foreground))',
                marginBottom: 'var(--spacing-2)'
              }}>
                {t('emailRequired')}
              </label>
              <input
                type="email"
                value={accountData.email}
                onChange={(e) => setAccountData({ ...accountData, email: e.target.value })}
                placeholder={t('emailPlaceholder')}
                className="w-full text-sm sm:text-base rounded-md border border-border px-3 py-2.5 sm:px-4 sm:py-3"
              />
            </div>

            <div>
              <label style={{
                display: 'block',
                fontSize: 'var(--text-sm)',
                fontWeight: 'var(--font-semibold)',
                color: 'hsl(var(--foreground))',
                marginBottom: 'var(--spacing-2)'
              }}>
                {t('passwordRequired')}
              </label>
              <input
                type="password"
                value={accountData.password}
                onChange={(e) => setAccountData({ ...accountData, password: e.target.value })}
                placeholder="••••••••"
                className="w-full text-sm sm:text-base rounded-md border border-border px-3 py-2.5 sm:px-4 sm:py-3"
              />
              <p style={{ fontSize: '0.75rem', marginTop: '0.375rem', color: accountData.password && !passwordValid(accountData.password) ? 'var(--danger)' : 'hsl(var(--muted-foreground))' }}>
                {t('passwordRule') || 'Au moins 8 caractères, une lettre et un chiffre.'}
              </p>
            </div>

            <div>
              <label style={{
                display: 'block',
                fontSize: 'var(--text-sm)',
                fontWeight: 'var(--font-semibold)',
                color: 'hsl(var(--foreground))',
                marginBottom: 'var(--spacing-2)'
              }}>
                {t('confirmPasswordRequired')}
              </label>
              <input
                type="password"
                value={accountData.confirmPassword}
                onChange={(e) => setAccountData({ ...accountData, confirmPassword: e.target.value })}
                placeholder="••••••••"
                className="w-full text-sm sm:text-base rounded-md border border-border px-3 py-2.5 sm:px-4 sm:py-3"
              />
              {accountData.confirmPassword && accountData.password !== accountData.confirmPassword && (
                <p style={{ fontSize: '0.75rem', marginTop: '0.375rem', color: 'var(--danger)' }}>
                  {t('passwordsMismatch') || 'Les mots de passe ne correspondent pas.'}
                </p>
              )}
            </div>
          </div>
        )}

        {/* Étape 2 : Entreprise */}
        {step === 2 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-5)' }}>
            <h3 style={{
              fontSize: 'var(--text-xl)',
              fontWeight: 'var(--font-bold)',
              color: 'hsl(var(--foreground))',
              marginBottom: 'var(--spacing-2)'
            }}>
              {t('companyInfo')}
            </h3>

            <div>
              <label style={{
                display: 'block',
                fontSize: 'var(--text-sm)',
                fontWeight: 'var(--font-semibold)',
                color: 'hsl(var(--foreground))',
                marginBottom: 'var(--spacing-2)'
              }}>
                {t('companyNameRequired')}
              </label>
              <input
                type="text"
                value={companyData.company_name}
                onChange={(e) => setCompanyData({ ...companyData, company_name: e.target.value })}
                placeholder="FretManager SARL"
                className="w-full text-sm sm:text-base rounded-md border border-border px-3 py-2.5 sm:px-4 sm:py-3"
              />
            </div>

            <div>
              <label style={{
                display: 'block',
                fontSize: 'var(--text-sm)',
                fontWeight: 'var(--font-semibold)',
                color: 'hsl(var(--foreground))',
                marginBottom: 'var(--spacing-2)'
              }}>
                {t('freightType')}
              </label>
              <select
                value={
                  companyData.transport_modes.length === 2 ? 'both'
                    : companyData.transport_modes[0] || 'aerien'
                }
                onChange={(e) => {
                  const val = e.target.value;
                  let modes, type;
                  if (val === 'both') {
                    modes = ['aerien', 'maritime'];
                    type = 'freight_forwarder';
                  } else if (val === 'maritime') {
                    modes = ['maritime'];
                    type = 'freight_forwarder';
                  } else {
                    modes = ['aerien'];
                    type = 'freight_forwarder';
                  }
                  setCompanyData({ ...companyData, transport_modes: modes, company_type: type });
                }}
                className="w-full text-sm sm:text-base rounded-md border border-border px-3 py-2.5 sm:px-4 sm:py-3 bg-white cursor-pointer"
              >
                <option value="aerien">{t('airFreight')}</option>
                <option value="maritime">{t('maritimeFreight')}</option>
                <option value="both">{t('airAndMaritimeFreight')}</option>
              </select>
            </div>

            <div>
              <label style={{
                display: 'block',
                fontSize: 'var(--text-sm)',
                fontWeight: 'var(--font-semibold)',
                color: 'hsl(var(--foreground))',
                marginBottom: 'var(--spacing-2)'
              }}>
                {t('shippingCountry')}
              </label>
              <select
                value={companyData.origin_country}
                onChange={(e) => setCompanyData({ ...companyData, origin_country: e.target.value })}
                className="w-full text-sm sm:text-base rounded-md border border-border px-3 py-2.5 sm:px-4 sm:py-3 bg-white cursor-pointer"
              >
                <option value="">{t('selectACountry')}</option>
                {DEFAULT_COUNTRIES.map(c => (
                  <option key={c.code} value={c.name}>{c.name}</option>
                ))}
              </select>
            </div>

          </div>
        )}

        {/* Étape 3 : Zone de livraison */}
        {step === 3 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-5)' }}>
            <h3 style={{
              fontSize: 'var(--text-xl)',
              fontWeight: 'var(--font-bold)',
              color: 'hsl(var(--foreground))',
              marginBottom: 'var(--spacing-2)'
            }}>
              {t('defaultDeliveryZone')}
            </h3>

            <div>
              <label style={{
                display: 'block',
                fontSize: 'var(--text-sm)',
                fontWeight: 'var(--font-semibold)',
                color: 'hsl(var(--foreground))',
                marginBottom: 'var(--spacing-2)'
              }}>
                {t('deliveryCountry')}
              </label>
              <select
                value={deliveryZone.destination_country}
                onChange={(e) => {
                  const newCountry = e.target.value;
                  setDeliveryZone({ ...deliveryZone, destination_country: newCountry, cities: [''] });
                }}
                className="w-full text-sm sm:text-base rounded-md border border-border px-3 py-2.5 sm:px-4 sm:py-3 bg-white cursor-pointer"
              >
                <option value="">{t('selectACountry')}</option>
                {DEFAULT_COUNTRIES.map(c => (
                  <option key={c.code} value={c.name}>{c.name}</option>
                ))}
              </select>
            </div>

            <div>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: 'var(--spacing-3)'
              }}>
                <label style={{
                  fontSize: 'var(--text-sm)',
                  fontWeight: 'var(--font-semibold)',
                  color: 'hsl(var(--foreground))'
                }}>
                  {t('deliveryCities')}
                </label>
                {(() => {
                  const cc = DEFAULT_COUNTRIES.find(c => c.name === deliveryZone.destination_country)?.code;
                  const avail = cc ? (DEFAULT_CITIES_BY_COUNTRY[cc] || []) : [];
                  const canAdd = avail.length > 0 && deliveryZone.cities.length < avail.length;
                  return (
                    <button
                      type="button"
                      onClick={addCity}
                      disabled={!canAdd}
                      style={{
                        padding: '6px 12px',
                        background: 'var(--accent-light)',
                        border: '1px solid var(--accent)',
                        borderRadius: 'var(--radius)',
                        color: 'var(--accent)',
                        fontSize: 'var(--text-sm)',
                        fontWeight: 'var(--font-medium)',
                        cursor: canAdd ? 'pointer' : 'not-allowed',
                        opacity: canAdd ? 1 : 0.5,
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px'
                      }}
                    >
                      <Plus size={16} />
                      {t('add')}
                    </button>
                  );
                })()}
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-2)' }}>
                {(() => {
                  const countryCode = DEFAULT_COUNTRIES.find(c => c.name === deliveryZone.destination_country)?.code;
                  const availableCities = countryCode ? (DEFAULT_CITIES_BY_COUNTRY[countryCode] || []) : [];

                  if (!deliveryZone.destination_country) {
                    return (
                      <p style={{ fontSize: 'var(--text-sm)', color: 'hsl(var(--muted-foreground))' }}>
                        {t('selectCountryFirst') || 'Sélectionnez d\'abord un pays de livraison.'}
                      </p>
                    );
                  }
                  if (availableCities.length === 0) {
                    return (
                      <p style={{ fontSize: 'var(--text-sm)', color: 'hsl(var(--muted-foreground))' }}>
                        {t('noCitiesForCountry') || 'Aucune ville prédéfinie pour ce pays. Vous pourrez les ajouter après la création.'}
                      </p>
                    );
                  }
                  return deliveryZone.cities.map((city, index) => {
                    // Villes déjà choisies dans les autres champs (évite les doublons).
                    const taken = deliveryZone.cities.filter((_, i) => i !== index);
                    return (
                      <div key={index} style={{ display: 'flex', gap: 'var(--spacing-2)' }}>
                        <select
                          value={city}
                          onChange={(e) => updateCity(index, e.target.value)}
                          className="flex-1 min-w-0 text-sm sm:text-base rounded-md border border-border px-3 py-2.5 sm:px-4 sm:py-3 bg-white cursor-pointer"
                        >
                          <option value="">{t('selectCity')}</option>
                          {availableCities
                            .filter(c => c.name === city || !taken.includes(c.name))
                            .map(c => (
                              <option key={c.code} value={c.name}>{c.name}</option>
                            ))}
                        </select>
                        {deliveryZone.cities.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeCity(index)}
                            style={{
                              padding: '12px',
                              background: 'transparent',
                              border: '1px solid var(--danger)',
                              borderRadius: 'var(--radius)',
                              color: 'var(--danger)',
                              cursor: 'pointer'
                            }}
                          >
                            <X size={18} />
                          </button>
                        )}
                      </div>
                    );
                  });
                })()}
              </div>
            </div>

            <div style={{
              padding: 'var(--spacing-3)',
              background: 'var(--accent-light)',
              borderRadius: 'var(--radius)',
              fontSize: 'var(--text-sm)',
              color: 'hsl(var(--muted-foreground))'
            }}>
              {t('canAddMoreLater')}
            </div>
          </div>
        )}

        {/* Navigation Buttons */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--spacing-3)',
          marginTop: 'var(--spacing-6)',
          paddingTop: 'var(--spacing-4)',
          borderTop: '1px solid hsl(var(--border))'
        }}>
          {step > 1 && (
            <button
              type="button"
              onClick={() => setStep(step - 1)}
              disabled={loading}
              className="btn-secondary-pro"
              style={{ flex: '0 0 auto', display: 'inline-flex', alignItems: 'center', gap: '0.375rem' }}
            >
              <ChevronLeft size={18} />
              {t('previous')}
            </button>
          )}

          <span style={{ flex: 1, textAlign: 'center', fontSize: '0.8125rem', color: 'hsl(var(--muted-foreground))' }}>
            {step} / 3
          </span>

          {step < 3 ? (
            <button
              type="button"
              onClick={() => setStep(step + 1)}
              disabled={!canGoNext()}
              className="btn-accent-pro"
              style={{ flex: '0 0 auto', display: 'inline-flex', alignItems: 'center', gap: '0.375rem', opacity: !canGoNext() ? 0.5 : 1 }}
            >
              {t('next')}
              <ChevronRight size={18} />
            </button>
          ) : (
            <button
              type="button"
              onClick={handleSubmit}
              disabled={!canGoNext() || loading}
              className="btn-accent-pro"
              style={{ flex: '0 0 auto', opacity: (!canGoNext() || loading) ? 0.5 : 1 }}
            >
              {loading ? t('creating') : t('createMyCompany')}
            </button>
          )}
        </div>

        {/* Link to Login */}
        <div style={{ textAlign: 'center', marginTop: 'var(--spacing-6)' }}>
          <p style={{ fontSize: 'var(--text-sm)', color: 'hsl(var(--muted-foreground))' }}>
            {t('alreadyHaveAccount')}{' '}
            <button
              type="button"
              onClick={() => navigate('/login')}
              style={{
                background: 'none',
                border: 'none',
                color: 'var(--accent)',
                fontWeight: 'var(--font-semibold)',
                cursor: 'pointer',
                textDecoration: 'underline'
              }}
            >
              {t('signIn')}
            </button>
          </p>
        </div>
      </div>
      </div>
    </div>
  );
};

export default RegisterCompany;
